import { useMutation } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { userAtom } from '../atoms/authAtoms.js';
import { useAuthContext } from '../providers/AuthContext.js';
import { MfaSmsError } from '../services/mfaSmsService.js';

/**
 * Hook for sending SMS verification code during MFA enrollment
 * Returns mutation with verification ID on success
 */
const useSendSmsVerificationCode = () => {
    const { mfaSmsService } = useAuthContext();
    const user = useAtomValue(userAtom);
    return useMutation({
        mutationFn: async ({ phoneNumber, recaptchaVerifier }) => {
            if (!user) {
                throw new MfaSmsError('auth/no-user', 'User not authenticated');
            }
            // Format phone number to E.164 format
            const formattedPhone = mfaSmsService.formatPhoneNumber(phoneNumber);
            // Validate phone number
            if (!mfaSmsService.validatePhoneNumber(formattedPhone)) {
                throw new MfaSmsError('auth/invalid-phone-number', 'Please enter a valid phone number');
            }
            // Send verification code
            const verificationId = await mfaSmsService.sendVerificationCode(user, formattedPhone, recaptchaVerifier);
            return verificationId;
        },
    });
};
/**
 * Hook for verifying SMS code and completing MFA enrollment
 * Invalidates /me query on success to refresh user data with updated MFA status
 */
const useVerifyAndEnrollMfaSms = () => {
    const { mfaSmsService, queryClient, queryKeys } = useAuthContext();
    const user = useAtomValue(userAtom);
    return useMutation({
        mutationFn: async ({ verificationId, verificationCode }) => {
            if (!user) {
                throw new MfaSmsError('auth/no-user', 'User not authenticated');
            }
            // Verify code and enroll MFA
            await mfaSmsService.verifyAndEnroll(user, verificationId, verificationCode);
            return { success: true };
        },
        onSuccess: () => {
            // Invalidate /me query to refresh user data with updated MFA enrollment status
            // This allows the onboarding flow to advance based on the new MFA status
            queryClient.invalidateQueries({
                queryKey: queryKeys.me,
            });
        },
    });
};

export { useSendSmsVerificationCode, useVerifyAndEnrollMfaSms };
//# sourceMappingURL=useEnrollMfaSms.js.map
